import { getServerSession } from 'next-auth';

import { getKeycloakIssuer, keycloakProvider } from './providers';
import { jwtHandler } from './utils';

import type { GetServerSidePropsContext, NextApiRequest, NextApiResponse } from 'next';
import type { ISODateString, NextAuthOptions } from 'next-auth';
import { jwtDecode } from 'jwt-decode';
import { redirect } from 'next/navigation';

import { logger } from '@lichtblick/logger';

// eslint-disable-next-line turbo/no-undeclared-env-vars
const isSecureCookie = process.env.NEXTAUTH_URL?.startsWith('https://') ?? !!process.env.VERCEL;

export const isSessionValid = (
  session: {
    access_token: string;
    error?: 'RefreshTokenError';
    errorDetails?: any;
    expires: ISODateString;
    user?: {
      email?: string | null;
      image?: string | null;
      name?: string | null;
    };
  } | null,
): boolean => {
  if (!session || !session.access_token || session.access_token === '') {
    return false;
  }

  if (session.error) {
    return false;
  }

  try {
    const decoded = jwtDecode(session.access_token);
    const now = Date.now() / 1000;

    return (decoded.exp ?? 0) > now;
  } catch (error) {
    return false;
  }
};

export const config = {
  providers: [keycloakProvider],
  session: {
    strategy: 'jwt',
  },
  pages: {
    signIn: '/',
    signOut: '/',
  },
  callbacks: {
    signIn: async ({ account }) => {
      const issuer = getKeycloakIssuer();
      logger.info('NextAuth sign-in callback invoked', {
        component: 'AuthService',
        operation: 'signIn',
        provider: account?.provider ?? 'unknown',
        hasIssuer: Boolean(issuer),
        issuerPreview: issuer.slice(0, 80),
      });

      return true;
    },
    jwt: jwtHandler,
    session: async ({ session, token }) => {
      if (session && token && typeof token.access_token === 'string') {
        return {
          ...session,
          access_token: token.access_token,
          refresh_token: token.refresh_token,
          error: token.error,
          errorDetails: token.errorDetails,
        };
      }

      return { ...session, access_token: '' };
    },
  },
  events: {
    signOut: async ({ token: { id_token } }) => {
      const issuer = getKeycloakIssuer();
      if (!issuer) {
        logger.warn('Skipping Keycloak logout call because issuer is not configured', {
          component: 'AuthService',
          operation: 'signOut',
        });
        return;
      }

      const logoutUrl = new URL(`${issuer}/protocol/openid-connect/logout`);
      logoutUrl.searchParams.set('id_token_hint', String(id_token ?? ''));
      logger.info('Calling Keycloak logout endpoint', {
        component: 'AuthService',
        operation: 'signOut',
        logoutUrlHost: logoutUrl.host,
        logoutUrlPath: logoutUrl.pathname,
      });
      await fetch(logoutUrl.toString());
    },
  },
  cookies: {
    // default cookie options
    sessionToken: {
      name: `${isSecureCookie ? '__Secure-' : ''}next-auth.session-token`,
      options: {
        httpOnly: true,
        sameSite: 'none',
        path: '/',
        secure: isSecureCookie,
      },
    },
    callbackUrl: {
      name: `${isSecureCookie ? '__Secure-' : ''}next-auth.callback-url`,
      options: {
        httpOnly: true,
        sameSite: 'none',
        path: '/',
        secure: isSecureCookie,
      },
    },
    csrfToken: {
      // Default to __Host- for CSRF token for additional protection if using useSecureCookies
      // NB: The `__Host-` prefix is stricter than the `__Secure-` prefix.
      name: `${isSecureCookie ? '__Host-' : ''}next-auth.csrf-token`,
      options: {
        httpOnly: true,
        sameSite: 'none',
        path: '/',
        secure: isSecureCookie,
      },
    },
    pkceCodeVerifier: {
      name: `${isSecureCookie ? '__Secure-' : ''}next-auth.pkce.code_verifier`,
      options: {
        httpOnly: true,
        sameSite: 'none',
        path: '/',
        secure: isSecureCookie,
        maxAge: 60 * 15, // 15 minutes in seconds
      },
    },
    state: {
      name: `${isSecureCookie ? '__Secure-' : ''}next-auth.state`,
      options: {
        httpOnly: true,
        sameSite: 'none',
        path: '/',
        secure: isSecureCookie,
        maxAge: 60 * 15, // 15 minutes in seconds
      },
    },
    nonce: {
      name: `${isSecureCookie ? '__Secure-' : ''}next-auth.nonce`,
      options: {
        httpOnly: true,
        sameSite: 'none',
        path: '/',
        secure: isSecureCookie,
      },
    },
  },
} satisfies NextAuthOptions;

// Use it in server contexts
export const auth = async (
  ...args: [GetServerSidePropsContext['req'], GetServerSidePropsContext['res']] | [NextApiRequest, NextApiResponse] | []
) => {
  return getServerSession({
    ...config,
    ...args,
  });
};

declare module 'next-auth' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Session {
    access_token?: string;
    refresh_token?: string;
    error?: 'RefreshTokenError';
    errorDetails?: any;
  }
}

declare module 'next-auth/jwt' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface JWT {
    access_token?: string;
    error?: 'RefreshTokenError';
    refresh_token?: string;
  }
}
